/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [true]
*/

import { ToastErrorRecord, useToastContext } from '@finbeat/react-toolkit';
import useAuth, { initialAuth } from 'hooks/useAuth';
import i18n from 'i18next';
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { clearTranslations } from 'utils/i18n';
import useSignalRConnection from 'utils/SignalRConnection';
import { useGeneralSettingsContext } from './GeneralSettingsProvider';

export type Notify = {
    Id: number;
    TypeId: number;
    TypeCode: string;
    Data: any;
};

export type BillNotify = {
    err: string;
    errRecs: ToastErrorRecord[];
    totalCnt: number;
};

export type EventSubscriptions = {
    key: string;
    type: string;
    callback: (message: string) => void;
};

export type NotifyContextType = {
    addSubscription: (event: EventSubscriptions) => void;
    removeSubscription: (key: string) => void;
    showBrowserNotification: (title: string, options?: NotificationOptions) => void;
};

const NotifyContext = React.createContext<NotifyContextType>({} as any);

type NotifyProviderProps = React.PropsWithChildren;

const NotifyProvider = ({ children }: NotifyProviderProps) => {
    const subscriptions = useRef<EventSubscriptions[]>([]);
    const { addToast, toasts } = useToastContext();
    const { t } = useTranslation();
    const { refetchWorkplaceSettings } = useGeneralSettingsContext();
    const { setAuth } = useAuth();

    const notifyFileLoadFinish = (info: BillNotify) => {
        if (info.err) {
            addToast({
                type: 'danger',
                autoDeleteTime: null,
                description: info.err,
            });
        } else {
            const err_cnt = info.errRecs.length;

            if (err_cnt) {
                addToast({
                    type: 'warning',
                    autoDeleteTime: null,
                    title: t('BackOfficeSomeRecsCantBeDownloadedMsg'),
                    description: t('BackOfficeTotalNumOfRecsWithErrMsg'),
                    data: info.errRecs,
                });
            } else {
                const tcount = info.totalCnt;
                addToast({
                    type: 'success',
                    title: t('BackOfficeLoadingSuccessfulMsg'),
                    description: 'Total number of records: ' + tcount,
                });
            }
        }
    };

    function toCamelCase(_: string, value: any) {
        if (value && typeof value === 'object') {
            for (const k in value) {
                if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
                    value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
                    delete value[k];
                }
            }
        }
        return value;
    }

    const handleNotifyEventReceived = (notification: Notify) => {
        try {
            switch (notification.TypeCode) {
                case 'TranslatorFileLoadStart':
                    addToast({
                        type: 'info',
                        description: t('BackOfficeFileUploadHasStarted'),
                    });
                    break;

                case 'TranslatorFileLoadFinish':
                    const data = JSON.parse(notification.Data, toCamelCase);
                    notifyFileLoadFinish(data);
                    break;

                case 'TranslatorReload':
                    clearTranslations();
                    i18n.reloadResources().then(() => i18n.changeLanguage(i18n.language));
                    break;

                case 'ReportCreateComplete':
                    break;

                case 'ReportCreateFailed':
                    break;

                case 'PermissionsUpdated':
                    setAuth((auth) =>
                        auth != null ? { ...auth, accessTokenValid: false } : initialAuth
                    );

                    break;

                case 'WorkplaceSettingsUpdated':
                    refetchWorkplaceSettings();
                    break;

                case 'BlackListFileImportComplete':
                    break;

                case 'BlackListFileImportFailed':
                    break;

                default:
                    break;
            }

            const triggers = subscriptions.current.filter((s) => s.type === notification.TypeCode);
            for (const t of triggers) {
                t.callback(notification.Data);
            }
        } catch (error) {
            console.log('ERROR!', error);
        }
    };

    useSignalRConnection(handleNotifyEventReceived);

    const showBrowserNotification = (title: string, options?: NotificationOptions) => {
        let notification: any = null;
        const addWarnNotification = () => {
            const desc = t('BackOfficeEnableBrowserNotificationsMsg');
            if (toasts.find((t) => t.description === desc) == null) {
                addToast({
                    type: 'danger',
                    description: desc,
                    autoDeleteTime: 5000,
                });
            }
        };

        if (!('Notification' in window)) {
            addWarnNotification();
        } else if (Notification.permission === 'granted') {
            notification = new Notification(title, options);
        } else if (Notification.permission !== 'denied') {
            Notification.requestPermission(function (permission) {
                if (permission === 'granted') {
                    notification = new Notification(title, options);
                } else {
                    addWarnNotification();
                }
            });
        } else {
            addWarnNotification();
        }

        if (notification != null) {
            notification.onshow = function () {
                setTimeout(function () {
                    notification.close();
                }, 2000);
            };
        }
    };

    return (
        <NotifyContext.Provider
            value={{
                addSubscription: (event) => {
                    subscriptions.current = [...subscriptions.current, event];
                },
                removeSubscription: (key: string) => {
                    subscriptions.current = subscriptions.current.filter((s) => s.key !== key);
                },
                showBrowserNotification,
            }}
        >
            {children}
        </NotifyContext.Provider>
    );
};

const useNotifyContext = () => useContext(NotifyContext);

export { NotifyProvider, useNotifyContext };
export default NotifyProvider;
